<template>
  <div class="seat-plan">
    <section class="bg-n3black text-white">
      <div class="sm:container sm:mx-auto">
        <BackButton
          :url="
            $router.resolve({
              name: 'eventDetail',
              params: { slug: event.slug },
            })
          "
        />
        <div class="sm:grid sm:grid-cols-12 sm:gap-4">
          <div class="col-span-12 sm:col-span-8">
            <div class="absolute top-0 w-full sm:relative">
              <div
                class="relative overflow-hidden sm:mr-10 min-h-screen sm:min-h-[600px]"
                :class="{
                  'loading-content':
                    fetchingSeats || fetchingShow || fetchingEvents,
                }"
              >
                <SvgPanZoom
                  v-if="show.ui && !fetchingSeats && !fetchingShow"
                  ref="panZoom"
                  style="width: 100%; border: 1px solid black"
                  :zoomEnabled="true"
                  :controlIconsEnabled="false"
                  :fit="true"
                  :center="true"
                  :zoomScaleSensitivity="0.5"
                  :mouseWheelZoomEnabled="false"
                  :dblClickZoomEnabled="false"
                  @created="registerSvgPanZoom"
                >
                  <seat-svg />
                </SvgPanZoom>
                <div class="pan-zoom-control">
                  <button @click="panZoomEvent('zoomOut')" class="mr-5">
                    <span class="material-icons text-[40px] text-neutral-700">
                      remove_circle
                    </span>
                  </button>

                  <button @click="panZoomEvent('zoomIn')">
                    <span class="material-icons text-[40px] text-neutral-700">
                      add_circle
                    </span>
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div
            class="col-span-12 sm:col-span-4"
            v-if="!(fetchingSeats || fetchingShow || fetchingEvents)"
          >
            <div class="h-full">
              <SeatSummary class="hidden sm:block" />
              <div class="fixed-bottom">
                <div class="seat-continue-button px-5 sm:px-0">
                  <default-button
                    :label="buttonLabel"
                    :disable="!selectedSeats.length"
                    class="w-full"
                    @clicked="
                      (s) =>
                        $router.push({
                          name: 'reservationSummary',
                        })
                    "
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="py-10 hidden sm:block">
          <div>
            <div class="grid grid-cols-12 gap-4" v-show="selectedSeats.length">
              <div class="col-span-2 text-[32px]">Seats selected:</div>
              <div class="col-span-10">
                <span
                  v-for="(s, index) in selectedSeats"
                  :key="index"
                  class="seat-label-chip"
                >
                  {{ seatNameShorter(s.label) }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
  <component :is="'script'" type="application/ld+json"> {{ jsonLd }}</component>
</template>
<script>
import {
  computed,
  onMounted,
  onUnmounted,
  reactive,
  toRefs,
} from "@vue/runtime-core";

import { SvgPanZoom } from "vue-svg-pan-zoom";
import { useStore } from "vuex";
import SeatSvg from "@/components/seat/SeatSvg.vue";
import SeatSummary from "@/components/seat/SeatSummary.vue";
import DefaultButton from "@/components/button/DefaultButton.vue";
import { moneyFormat, scrollToTop, seatNameShorter } from "@/utils/helper";
import { useRoute } from "vue-router";
import BackButton from "@/components/button/BackButton.vue";
import { useMeta } from "vue-meta";
import { useGoogleEventCard } from "@/composable/useGoogleEventCard";

import { isMobile } from "mobile-device-detect";
export default {
  components: {
    SvgPanZoom,
    SeatSvg,
    SeatSummary,
    DefaultButton,
    BackButton,
  },
  setup() {
    const store = useStore();
    const route = useRoute();
    const { json, addEvent, resetEvents } = useGoogleEventCard();

    const state = reactive({
      svgPanZoomInstance: null,
      event: computed(() => store.state.event.event),
      show: computed(() => store.state.show.show),
      seatCategories: computed(() => store.getters["show/seatCategories"]),
      standingAvailable: computed(
        () => store.getters["show/standingAvailable"]
      ),
      reservation: computed(() => store.state.reservation.reservation),
      selectedSeats: computed(() => store.state.reservation.selectedSeats),
      reservationID: computed(() => store.state.reservation.reservationID),
      fetchingEvents: computed(() => store.state.event.fetchingData),
      fetchingShow: computed(() => store.state.show.fetchingData),
      fetchingSeats: computed(() => store.state.show.fetchingSeats),
      buttonLabel: computed(() => {
        let label = "Continue";
        if (state.selectedSeats.length) {
          label = `${state.selectedSeats.length} ticket${
            state.selectedSeats.length > 1 ? "s" : ""
          } for ${moneyFormat(
            state.reservation.price,
            state.show.paymentCurrency
          )}`;
        } else if (state.selectedSeats.length === 0) {
          label = "Please select tickets";
        }
        return label;
      }),
      jsonLd: computed(() => {
        if (json.length) resetEvents();
        if (state.show.movie.title) {
          addEvent({
            title: state.show.movie.title,
            start: state.show.start.toDate(),
            end: state.show.start.toDate(),
            description: state.show.movie.description,
            image: state.show.movie.posterUrlLandscape,
            place: {
              name: state.show.cinema.name,
              street: state.show.cinema.street,
              city: state.show.cinema.city,
              postal: state.show.cinema.postal,
            },
          });
        }

        return json;
      }),
    });

    useMeta(
      computed(() => ({
        title: (state.event?.title ?? "Event") + " - Seating plan",
        description: state.event?.description ?? "",
      }))
    );

    onMounted(async () => {
      scrollToTop();
      fetchSeats();
    });

    onUnmounted(() => {
      store.dispatch("show/unsubscribeSeatSeatSnapshot");
    });

    function fetchSeats() {
      store.dispatch("show/findShow", route.params.showSlug).then(() => {
        store.dispatch("show/fetchShowSeats");
      });
    }

    function registerSvgPanZoom(panZoom) {
      state.svgPanZoomInstance = panZoom;
    }

    function panZoomEvent(e) {
      state.svgPanZoomInstance[e]();
    }
    function handleClick() {
      alert("Clicked");
    }

    return {
      ...toRefs(state),
      registerSvgPanZoom,
      panZoomEvent,
      seatNameShorter,
      fetchSeats,
      isMobile,
      handleClick,
    };
  },
};
</script>
